import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal, FadeUp } from "@/components/motion";
import EditorialMarquee from "@/components/EditorialMarquee";
import { IMAGES, ABOUT_LEAD, CHAPTERS, PRODUCTS } from "@/data/content";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Offshore energy platform and tanker"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-paper via-paper/60 to-paper/25" />
      <div className="absolute inset-0 bg-gradient-to-b from-paper/85 via-paper/20 to-transparent" />

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-20 sm:pb-28 pt-40">
        <FadeUp delay={0.15}>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">
            Energy Trading · Sharjah, UAE
          </p>
        </FadeUp>

        <MaskedLines
          className="mt-8 font-display font-medium uppercase tracking-tighter leading-[0.95] text-[15vw] sm:text-[12vw] lg:text-[8.5rem] text-ink"
          delay={0.3}
          lines={[
            "Powering",
            "Global",
            <span key="energy">
              Energy<span className="text-brand">.</span>
            </span>,
          ]}
        />

        <div className="mt-12 flex flex-col sm:flex-row sm:items-end justify-between gap-10">
          <FadeUp delay={1.0} className="max-w-md">
            <p className="text-black/65 font-light leading-relaxed text-base sm:text-lg">
              AA GULF FZE — a leading force in the energy trading industry, connecting
              producers and markets from the heart of Dubai.
            </p>
          </FadeUp>
          <FadeUp delay={1.15} className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-2 bg-brand text-white px-8 py-4 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-brand-hover transition-colors duration-300"
            >
              Start a Conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/products"
              data-testid="hero-cta-products"
              className="group inline-flex items-center gap-2 border border-black/25 text-ink px-8 py-4 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-ink hover:text-paper transition-colors duration-300"
            >
              Explore Products
            </Link>
          </FadeUp>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-6 lg:right-8 z-10 hidden sm:flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/40 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="w-4 h-4 text-brand" />
        </motion.span>
      </motion.div>
    </section>
  );
}

function AboutTeaser() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section data-testid="about-teaser" className="py-24 sm:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="md:col-span-6">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">About Us</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-medium uppercase tracking-tight text-4xl sm:text-5xl leading-[1.05] text-ink">
              A leading force in energy trading, from the heart of Dubai<span className="text-brand">.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-black/60 font-light leading-relaxed max-w-lg">{ABOUT_LEAD}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/about"
              data-testid="about-teaser-link"
              className="mt-10 inline-flex group items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-ink hover:text-brand transition-colors duration-300"
            >
              Our Story
              <ArrowUpRight className="w-4 h-4 text-brand transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="md:col-span-5 md:col-start-8" ref={ref}>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden aspect-[3/4]">
              <motion.img
                style={{ y }}
                src={IMAGES.rigSunset}
                alt="Offshore energy platform at sunset"
                className="absolute inset-0 w-full h-[116%] object-cover scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.3em] uppercase text-white/80">
                Sharjah · UAE
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  return (
    <section data-testid="why-choose-us" className="py-24 sm:py-32 lg:py-40 border-t border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-12 lg:gap-16">
        <div className="md:col-span-5">
          <div className="md:sticky md:top-32">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">Why Choose Us</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-medium uppercase tracking-tight text-4xl sm:text-5xl leading-[1.05] text-ink">
                The Dubai<br />Advantage<span className="text-brand">.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-black/60 font-light leading-relaxed max-w-sm">
                Six structural reasons partners choose AA GULF FZE as their gateway to
                global energy markets.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="md:col-span-7">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.04}>
              <div
                data-testid={`chapter-${c.num}`}
                className="group border-t border-black/10 py-8 sm:py-10 grid grid-cols-12 gap-4 items-baseline hover:border-brand/60 transition-colors duration-500"
              >
                <span className="col-span-3 sm:col-span-2 font-mono text-sm text-brand">{c.num}</span>
                <div className="col-span-9 sm:col-span-10">
                  <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-ink transition-transform duration-500 group-hover:translate-x-2">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-black/55 font-light leading-relaxed max-w-lg">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-black/10" />
        </div>
      </div>
    </section>
  );
}

function ProductsTeaser() {
  return (
    <section data-testid="products-teaser" className="py-24 sm:py-32 lg:py-40 border-t border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">Our Products</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-medium uppercase tracking-tight text-4xl sm:text-5xl text-ink">
                What we trade<span className="text-brand">.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/products"
              data-testid="products-teaser-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-black/60 hover:text-brand transition-colors duration-300"
            >
              View All Products
              <ArrowUpRight className="w-4 h-4 text-brand transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.code} delay={i * 0.08}>
              <Link
                to="/products"
                data-testid={`product-card-${p.code.toLowerCase()}`}
                className="group relative block overflow-hidden aspect-[3/4] bg-mist"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-transparent" />
                <ArrowUpRight className="absolute top-4 right-4 w-5 h-5 text-white opacity-0 -translate-x-1 translate-y-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-brand">{p.code}</p>
                  <h3 className="mt-2 font-display text-xl sm:text-2xl font-medium uppercase tracking-tight text-white">
                    {p.name}
                  </h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="cta-band" className="relative overflow-hidden border-t border-black/10">
      <motion.div style={{ y }} className="absolute inset-0 scale-125">
        <img src={IMAGES.meeting} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-paper/85" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-28 sm:py-36">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">Contact Us</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display font-medium uppercase tracking-tight text-4xl sm:text-6xl leading-[1.02] max-w-4xl text-ink">
            Collaborate with us for exciting business opportunities<span className="text-brand">.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            data-testid="cta-band-button"
            className="mt-10 group inline-flex items-center gap-2 bg-brand text-white px-8 py-4 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-brand-hover transition-colors duration-300"
          >
            Contact Us
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <AboutTeaser />
      <WhyChooseUs />
      <ProductsTeaser />
      <CtaBand />
    </div>
  );
}
